import { CommonModule } from '@angular/common';
import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {extractErrorMessage, goBack} from 'src/app/common/common';
import {PlatformDto, PlatformViewModel} from 'src/app/model/platform.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';

@Component({
  selector: 'app-create-platform',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './create-platform.component.html',
  styleUrl: './create-platform.component.scss'
})
export class CreatePlatformComponent implements OnInit, OnDestroy {

  // Form
  platformForm = new FormGroup({
    environment: new FormControl<string>('', [Validators.required]),
    x_energy_enabled: new FormControl<boolean>(false, [Validators.required]),
    amqp_host: new FormControl<string | undefined>(''),
    amqp_user: new FormControl<string | undefined>(''),
    amqp_pass: new FormControl<string | undefined>(''),
    exchange: new FormControl<string | undefined>(''),
    routing_key: new FormControl<string | undefined>(''),
  });

  unsubscribe = new Subject<void>();
  formsSubscribe: any;

  public editing: boolean = false;
  public platformId: string = '';
  public error: string | null = null;
  public router: Router;

  public platform: PlatformDto = {environment: "0", x_energy_enabled: true};

  constructor(private injector: Injector, private platformService: ApiService) {
    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.editing = true;
        this.platformId = event.snapshot.params.id;
      }
    });
  }

  setFormsDefaultValue(platform?: PlatformViewModel) {
    this.platformForm.controls.environment.setValue(platform?.environment || '');
    this.platformForm.controls.x_energy_enabled.setValue(!!platform?.x_energy_enabled);

    // Always set AMQP values from the platform model (especially important for editing)
    this.platformForm.controls.amqp_host.setValue(platform?.amqp_host || '');
    this.platformForm.controls.amqp_user.setValue(platform?.amqp_user || '');
    this.platformForm.controls.amqp_pass.setValue(platform?.amqp_pass || '');
    this.platformForm.controls.exchange.setValue(platform?.exchange || '');
    this.platformForm.controls.routing_key.setValue(platform?.routing_key || '');

    if (this.editing) {
      this.platformForm.controls.environment.disable();
    }

    // Update validators based on x_energy_enabled value
    this.updateAmqpValidators();
  }

  updateAmqpValidators() {
    const isXEnergyEnabled = this.platformForm.controls.x_energy_enabled.value;

    if (isXEnergyEnabled) {
      // Remove required validators when X-Energy is enabled
      this.platformForm.controls.amqp_host.clearValidators();
      this.platformForm.controls.amqp_user.clearValidators();
      this.platformForm.controls.amqp_pass.clearValidators();
      this.platformForm.controls.exchange.clearValidators();
      this.platformForm.controls.routing_key.clearValidators();
    } else {
      // Add required validators when X-Energy is disabled
      this.platformForm.controls.amqp_host.setValidators([Validators.required]);
      this.platformForm.controls.amqp_user.setValidators([Validators.required]);
      this.platformForm.controls.amqp_pass.setValidators([Validators.required]);
      this.platformForm.controls.exchange.setValidators([Validators.required]);
      this.platformForm.controls.routing_key.setValidators([Validators.required]);
    }

    // Update validity status
    this.platformForm.controls.amqp_host.updateValueAndValidity();
    this.platformForm.controls.amqp_user.updateValueAndValidity();
    this.platformForm.controls.amqp_pass.updateValueAndValidity();
    this.platformForm.controls.exchange.updateValueAndValidity();
    this.platformForm.controls.routing_key.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.setupFormListeners();

    if (this.editing) {
      const queryParams: CustomHttpParams = {
        'depth': 1,
        'environment': this.platformId
      }
      this.platformService.getPlatform(queryParams)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (response: PlatformViewModel[]) => {
            if (response && response.length > 0) {
              this.setFormsDefaultValue(response[0]);
            }
          },
          error: (err) => {
            console.error(err);
            this.error = extractErrorMessage(err, "Failed to load platform data.");
          }
        });
    } else {
      // For new platforms, set default and update validators
      this.setFormsDefaultValue();
    }
  }

  setupFormListeners() {
    this.formsSubscribe = this.platformForm.controls.environment.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.platform.environment = val || '');

    this.formsSubscribe = this.platformForm.controls.x_energy_enabled.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        // First update the platform model with the form control value
        this.platform.x_energy_enabled = !!val;

        // Then clear AMQP details if X-Energy is enabled
        if (this.platform.x_energy_enabled) {
          this.platform = {
            ...this.platform,
            amqp_host: '',
            amqp_user: '',
            amqp_pass: '',
            exchange: '',
            routing_key: ''
          };

          // Also clear the form controls for AMQP fields
          this.platformForm.controls.amqp_host.setValue('');
          this.platformForm.controls.amqp_user.setValue('');
          this.platformForm.controls.amqp_pass.setValue('');
          this.platformForm.controls.exchange.setValue('');
          this.platformForm.controls.routing_key.setValue('');
        }

        // Update validators based on new x_energy_enabled value
        this.updateAmqpValidators();
      });

    this.formsSubscribe = this.platformForm.controls.amqp_host.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.platform.amqp_host = val || '');

    this.formsSubscribe = this.platformForm.controls.amqp_user.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.platform.amqp_user = val || '');

    this.formsSubscribe = this.platformForm.controls.amqp_pass.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.platform.amqp_pass = val || '');

    this.formsSubscribe = this.platformForm.controls.exchange.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.platform.exchange = val || '');

    this.formsSubscribe = this.platformForm.controls.routing_key.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.platform.routing_key = val || '');
  }

  save() {
    if (this.platformForm.invalid) {
      this.error = 'Please fill out all required fields correctly and try again.';
      return;
    }

    const amqpHost = this.platform?.amqp_host;
    if (!this.platform.x_energy_enabled && amqpHost) {
      if (amqpHost.includes("amqp://")) {
        this.error = 'Please do not include amqp:// in amqp hostname.';
        return;
      }

      if (!amqpHost.includes("//")) {
        this.error = 'Please include virtual host at the end of the amqp hostname.';
        return;
      }
    }

    if (this.editing) {
      this.platformService.editPlatform(this.platform).subscribe({
        next: () => {
          goBack('/platforms', this.router);
        },
        error: (err) => {
          console.error(err);
          this.error = typeof err === 'string' ? err : "Failed to update platform.";
        }
      });
    } else {
      this.platformService.createPlatform(this.platform).subscribe({
        next: () => {
          goBack('/platforms', this.router);
        },
        error: (err) => {
          console.error(err);
          this.error = typeof err === 'string' ? err : "Failed to create platform.";
        }
      });
    }
  }

  backPage(url: string, router: Router) {
    goBack(url, router);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
